import React, { useEffect, useState } from 'react';
import MerchantContextPanel from '../components/MerchantContextPanel';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2 } from 'lucide-react';

// Step Components
import Step1BusinessInfo from '../components/wizard/Step1BusinessInfo';
import Step2OwnerInfo from '../components/wizard/Step2OwnerInfo';
import Step3Pricing from '../components/wizard/Step3Pricing';
import Step4Equipment from '../components/wizard/Step4Equipment';
import Step5Documents from '../components/wizard/Step5Documents';
import Step6Review from '../components/wizard/Step6Review';

const STEPS = [
  { number: 1, name: 'Business Info', component: Step1BusinessInfo },
  { number: 2, name: 'Owner Info', component: Step2OwnerInfo },
  { number: 3, name: 'Pricing', component: Step3Pricing },
  { number: 4, name: 'Equipment', component: Step4Equipment },
  { number: 5, name: 'Documents', component: Step5Documents },
  { number: 6, name: 'Review', component: Step6Review }
];

function ApplicationWizardPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => { loadApplication(); }, [appId]);

  const loadApplication = async () => {
    try {
      const data = await api.getApplication(appId);
      const sections = Array.isArray(data.sections) ? data.sections : [];
      setApplication({ ...data, sections });
      const incomplete = sections.find(s => !s.completed);
      if (incomplete) setCurrentStep(incomplete.step_number);
    } catch (error) {
      setLoadError(error.message || 'This application could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  const handleStepSave = async (stepNumber, stepData) => {
    try {
      setSaving(true);
      await api.updateApplication(appId, stepNumber, stepData);
      await loadApplication();
      return true;
    } catch (error) {
      console.error('Failed to save step:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (stepData) => {
    const saved = await handleStepSave(currentStep, stepData);
    if (saved && currentStep < STEPS.length) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSaveDraft = async (stepData) => {
    await handleStepSave(currentStep, stepData);
    navigate('/applications');
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await api.submitApplication(appId);
      navigate('/applications');
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3b82f6]" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12" data-testid="application-load-error">
        <p className="text-muted-foreground">{loadError || 'Application not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/applications')} data-testid="application-back-btn">Back to applications</Button>
      </div>
    );
  }

  const progressPct = (currentStep / STEPS.length) * 100;
  const StepComponent = STEPS[currentStep - 1].component;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="wizard-title">Merchant Application</h1>
          {application.business_name && (
            <p className="text-sm text-muted-foreground mt-0.5">{application.business_name}</p>
          )}
        </div>
        <span className="text-sm text-muted-foreground font-medium" data-testid="step-indicator">
          Step {currentStep} of {STEPS.length}
        </span>
      </div>

      {/* Canonical merchant context — same component/API as Leads, Opportunities, Proposals */}
      <MerchantContextPanel businessId={application.business_id} recordType="application" recordId={application.id} />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-[#3b82f6] h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Step Navigation */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, idx) => {
          const section = application.sections?.find(s => s.step_number === step.number);
          const isCompleted = section?.completed;
          const isCurrent = currentStep === step.number;
          const isPast = currentStep > step.number;

          return (
            <button
              key={step.number}
              type="button"
              onClick={() => setCurrentStep(step.number)}
              className="flex flex-col items-center gap-1 group relative"
              data-testid={`step-nav-${step.number}`}
            >
              {/* Step indicator dot/check */}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                isCompleted
                  ? 'bg-green-500 text-white'
                  : isCurrent
                  ? 'bg-[#3b82f6] text-white'
                  : 'bg-gray-200 text-muted-foreground'
              }`}>
                {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : step.number}
              </div>
              {/* Step name */}
              <span className={`text-xs font-medium transition-colors ${
                isCurrent
                  ? 'text-[#3b82f6]'
                  : isCompleted
                  ? 'text-green-600'
                  : 'text-muted-foreground'
              }`}>
                {step.name}
              </span>
              {/* Active underline */}
              {isCurrent && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#3b82f6] rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Step Content */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <StepComponent
            application={application}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            onGoToStep={(step) => setCurrentStep(step)}
            saving={saving}
            isLastStep={currentStep === STEPS.length}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default ApplicationWizardPage;
